import { FC, memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Alert, TableCell, TableRow, Typography, useTheme } from "@mui/material";
import { IFamilyConfig, ILightingClassConfig, IRoadLayoutConfig, IRoadwayConfig, IOptimizationConfig } from "types";
import { Table, TableContainer, TableHead, Paper } from "@mui/material";
import { setObjectURL } from "utils/urls";
import type { IResultItem } from "../../types";
import { filterValidOptions, getFamilyDescription, getClassTypeFlags } from "../../common/utils";
import LightingParameters from "./LightingParameters";
import Roadway from "./Roadway";
import BorderArea from "./BorderArea";
import Selection from "./Selection";
import getStyles from "./styles";

type Props = {
  tab: string;
  familyConfig: IFamilyConfig;
  lightingClassConfig: ILightingClassConfig;
  roadLayoutConfig: IRoadLayoutConfig;
  roadwayConfig: IRoadwayConfig;
  optimizationConfig: IOptimizationConfig;
  families: any;
  options: IResultItem[];
  callback: any;
  selected: string | number;
  setResultFamilyIndex: any;
  resultFamilyIndex: number;
};

const ResultsTable: FC<Props> = ({
  tab,
  familyConfig,
  lightingClassConfig,
  roadLayoutConfig,
  roadwayConfig,
  optimizationConfig,
  families,
  options,
  callback,
  selected,
  setResultFamilyIndex,
  resultFamilyIndex,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).Results, [theme]);
  const filteredOptions = useMemo(() => filterValidOptions(options ?? []), [options]);
  const family_description = useMemo(() => getFamilyDescription(families, familyConfig), [families, familyConfig]);
  const { isRoadClassM, isRoadClassC, isRoadClassP, isLeftBorderClassP, isLeftBorderClassC, isRightBorderClassP, isRightBorderClassC } = useMemo(
    () => getClassTypeFlags(lightingClassConfig),
    [lightingClassConfig]
  );

  const roadClasses = lightingClassConfig?.roadClasses;
  const leftBorderClass = lightingClassConfig?.borderAreaLeftClasses;
  const rightBorderClass = lightingClassConfig?.borderAreaRightClasses;
  const customRoadClass = lightingClassConfig?.customClasses?.mainCustomClass;
  const customLeftBorderClass = lightingClassConfig?.customClasses?.leftBorderCustomClass;
  const customRightBorderClass = lightingClassConfig?.customClasses?.rightBorderCustomClass;

  const handleSelectionChange = useCallback(
    ({ name, family_id, familyIndex }: { name: string; family_id: string; familyIndex: number }) => {
      console.log("Selected:", name, family_id, familyIndex);
      callback(name);
      setObjectURL({ family_id, familyIndex });
      setResultFamilyIndex(familyIndex);
    },
    [callback, setObjectURL, setResultFamilyIndex]
  );

  if (!options || options.length === 0) {
    return (
      <Alert variant="outlined" severity="error" icon={false}>
        <Typography variant="body2">{t("No results found. Please change your settings.")}</Typography>
      </Alert>
    );
  }

  if (filteredOptions?.length < 1) return null;

  return (
    <TableContainer component={Paper} sx={styles.container}>
      <Table size="small" sx={styles.table}>
        <TableHead sx={styles.head}>
          <TableRow>
            <TableCell />
            {filteredOptions.map((lum, index) => (
              <TableCell key={"lum" + index} scope="row">
                {lum.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <LightingParameters
          tab={tab}
          families={families}
          description={family_description}
          options={filteredOptions}
          optimizationType={optimizationConfig.optimizationType}
          roadLength={roadwayConfig.roadLength}
        />

        <Roadway
          options={filteredOptions}
          roadClasses={roadClasses}
          customRoadClass={customRoadClass}
          isRoadClassM={isRoadClassM}
          isRoadClassC={isRoadClassC}
          isRoadClassP={isRoadClassP}
        />

        {roadLayoutConfig.borderAreaLeft === true && (
          <BorderArea
            side="left"
            options={filteredOptions}
            borderClass={leftBorderClass}
            customBorderClass={customLeftBorderClass}
            isBorderClassP={isLeftBorderClassP}
            isBorderClassC={isLeftBorderClassC}
          />
        )}

        {roadLayoutConfig.borderAreaRight === true && (
          <BorderArea
            side="right"
            options={filteredOptions}
            borderClass={rightBorderClass}
            customBorderClass={customRightBorderClass}
            isBorderClassP={isRightBorderClassP}
            isBorderClassC={isRightBorderClassC}
          />
        )}

        <Selection options={filteredOptions} selected={selected} resultFamilyIndex={resultFamilyIndex} onChange={handleSelectionChange} />
      </Table>
    </TableContainer>
  );
};

export default memo(ResultsTable);
